import { useCallback, useEffect, useRef, useState } from "react";
import { getExerciseFeedback } from "../components/funtionsBD/Mainservice";
import { getSocket } from "../socket";

const KOT_FEEDBACK_POLL_MS = 12_000;

function feedbacksShallowEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((item, i) => {
    const other = b[i];
    if (item?.message_id !== other?.message_id) return false;
    if (item?.video_message_url !== other?.video_message_url) return false;
    if (item?.text_message !== other?.text_message) return false;
    return (
      JSON.stringify(item?.view_setup ?? null) ===
      JSON.stringify(other?.view_setup ?? null)
    );
  });
}

/**
 * Loads exercise feedbacks for KoT perf-tab video mapping.
 * Polls while `pollWhen` is true and listens for `kotMatchVideoUploaded` socket events.
 */
export function useKotExerciseFeedbacks({ open, teId, pollWhen = false }) {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const teIdRef = useRef(teId);
  teIdRef.current = teId;

  const reloadFeedbacks = useCallback(async () => {
    const tid = teIdRef.current;
    if (!tid) return [];
    try {
      const res = await getExerciseFeedback({ te_id: tid });
      const next = res?.data?.data?.feedbacks || [];
      setFeedbacks((prev) => (feedbacksShallowEqual(prev, next) ? prev : next));
      return next;
    } catch {
      setFeedbacks([]);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!open || !teId) {
      setFeedbacks([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await getExerciseFeedback({ te_id: teId });
        if (!cancelled) {
          const next = res?.data?.data?.feedbacks || [];
          setFeedbacks((prev) => (feedbacksShallowEqual(prev, next) ? prev : next));
        }
      } catch {
        if (!cancelled) setFeedbacks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, teId]);

  useEffect(() => {
    if (!open || !teId || !pollWhen) return undefined;
    reloadFeedbacks();
    const id = setInterval(reloadFeedbacks, KOT_FEEDBACK_POLL_MS);
    return () => clearInterval(id);
  }, [open, teId, pollWhen, reloadFeedbacks]);

  useEffect(() => {
    if (!open || !teId) return undefined;
    const socket = getSocket();
    if (!socket) return undefined;

    const onKotVideoUploaded = (payload) => {
      if (!payload) return;
      if (String(payload.te_id) !== String(teIdRef.current)) return;
      reloadFeedbacks();
    };

    const onNewMessage = (msg) => {
      if (!msg?.video_message_url) return;
      const type = String(msg.message_type || "").toLowerCase();
      if (type !== "evaluation" && type !== "video") return;
      if (String(msg.te_id) !== String(teIdRef.current)) return;
      if (!String(msg.text_message || "").includes("kot_match_id:")) return;
      reloadFeedbacks();
    };

    socket.on("kotMatchVideoUploaded", onKotVideoUploaded);
    socket.on("newMessage", onNewMessage);
    return () => {
      socket.off("kotMatchVideoUploaded", onKotVideoUploaded);
      socket.off("newMessage", onNewMessage);
    };
  }, [open, teId, reloadFeedbacks]);

  return { feedbacks, loading, reloadFeedbacks, setFeedbacks };
}
